import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { PlusIcon } from "@icons";
import { useUser } from "@hooks/useUser";
import { Button } from "@design-system/Button/Button";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { useCallback, useMemo } from "react";
import {
    AddMemberOptions,
    MemberManagementOverlayEntity,
} from "@components/memberManagement/memberManagement.types";
import { useOverlays } from "contexts/OverlayContext";
import { InviteSchema, MemberSchema } from "@common/schemas/client.schema";
import styles from "../ClientsView.module.scss";
import { useUnaffiliatedMembers } from "../../useUnaffiliatedMembers";
import { ClientTeamList } from "../../TeamList/ClientTeamList";

type MembersCellEntity = Omit<MemberManagementOverlayEntity, "user">;

interface MembersCellRow {
    id: string;
    name: string;
    type: "workspace" | "entity" | "close";
    workspaceId?: string;
    entityId?: number;
    members?: MemberSchema[];
    invites?: InviteSchema[];
    entities?: MembersCellEntity[];
}

export const MembersCell = <TRow extends MembersCellRow>({
    row,
}: CellContext<ViewTableFeatures, TRow, unknown>) => {
    const { openOverlay } = useOverlays();
    const user = useUser();
    const actingUserAssignment = row.original.members?.find(
        (assignment) => assignment.userId === user.id,
    );
    const workspace = row.getParentRow()?.original ?? row.original;

    const actingUserRoleInWorkspace = actingUserAssignment?.role;
    const members = row.original.members;
    const invites = row.original.invites;

    const nonTeamMembers = useUnaffiliatedMembers(members ?? []);

    const entityId =
        row.original.type === "entity"
            ? (row.original.entityId ?? Number(row.original.id))
            : undefined;
    const options: AddMemberOptions | undefined = useMemo(
        () => (entityId ? { initialEntityIds: [entityId] } : undefined),
        [entityId],
    );
    const addMemberOverlayPayload = useMemo(
        () => ({
            workspaceId: workspace.workspaceId ?? workspace.id,
            entities: (workspace.entities ?? []).map((entity) => ({
                ...entity,
                user: null,
            })),
            actingUserRole: actingUserRoleInWorkspace,
            addMemberHeader: `Add member to ${workspace.name}`,
            options,
        }),
        [
            actingUserRoleInWorkspace,
            options,
            workspace.entities,
            workspace.id,
            workspace.name,
            workspace.workspaceId,
        ],
    );
    const handleOpenAddMember = useCallback(
        () => openOverlay("memberManagementAddMember", addMemberOverlayPayload),
        [addMemberOverlayPayload, openOverlay],
    );
    const addMemberComponent = useMemo(
        () =>
            !invites?.length && !nonTeamMembers?.length ? (
                <EditableCellContent isEmpty onClick={handleOpenAddMember} />
            ) : (
                <Button
                    variant="primary"
                    iconOnly
                    size="sm"
                    className={styles.addMember}
                    onClick={handleOpenAddMember}
                    aria-label="Add member"
                >
                    <PlusIcon />
                </Button>
            ),
        [handleOpenAddMember, invites?.length, nonTeamMembers?.length],
    );

    if (row.original.type === "close") {
        return null;
    }

    return (
        <CaptureClicks>
            <ClientTeamList
                members={nonTeamMembers}
                invites={workspace.invites ?? []}
                appendContent={
                    actingUserRoleInWorkspace ? addMemberComponent : null
                }
                iconSize="sm"
            />
        </CaptureClicks>
    );
};
